/**
 * Graph Query Builder - Main query builder for graph database queries
 *
 * Primary entry point for building graph database queries from entity blocks.
 * Handles single and multi-entity queries with proper relationship chaining.
 *
 * @module queryBuilderCore/builders/graphQueryBuilder
 */

import { buildInterEntityRelationships } from '@/sections/insights/queryBuilder/legacy/builders/interEntityBuilder';
import {
  EntityBlock,
  GraphQuery,
  LogicalOperator,
} from '@/sections/insights/queryBuilder/legacy/types/types';

import { buildNodeFromBlock } from './nodeBuilder';
import { buildVulnerabilityQuery } from './vulnerabilityQueryBuilder';

/**
 * Builds a complete graph query from entity blocks and logical operators
 *
 * This is the main export function that orchestrates the entire query building process.
 * It handles single-entity queries, multi-entity queries with relationships, and
 * special cases like vulnerability queries.
 *
 * @param blocks - Array of entity blocks to query
 * @param interGroupOperators - Logical operators between blocks (AND/OR)
 * @param assetFilterOptions - Available asset filter options
 * @param vulnFilterOptions - Available vulnerability filter options
 * @param technologyFilterOptions - Available technology filter options
 * @param seedFilterOptions - Available seed filter options
 * @param integrationValues - List of integration source values to exclude
 * @param queryContentTab - Current query content tab context
 * @param riskKey - Optional risk key for asset vulnerability queries
 * @returns Complete graph query structure or null if invalid
 */
export function buildGraphQuery(
  blocks: EntityBlock[],
  interGroupOperators: LogicalOperator[] = [],
  assetFilterOptions?: Record<string, unknown>,
  vulnFilterOptions?: Record<string, unknown>,
  technologyFilterOptions?: Record<string, unknown>,
  seedFilterOptions?: Record<string, unknown>,
  integrationValues?: string[],
  queryContentTab?: string,
  riskKey?: string
): GraphQuery | null {
  if (blocks.length === 0) return null;

  const validBlocks = blocks.filter(block => block.entityType);
  if (validBlocks.length === 0) return null;

  const hasOrBetweenEntities = interGroupOperators.some(op => op === 'OR');
  if (hasOrBetweenEntities && validBlocks.length > 1) {
    console.warn(
      'OR between different entity types detected. ' +
        'Query will be split into multiple backend queries to work around Neo4j limitations.'
    );
  }

  const primaryBlock = validBlocks[0];
  if (!primaryBlock || !primaryBlock.entityType) {
    return null;
  }

  if (primaryBlock.entityType === 'Vulnerability') {
    const vulnerabilityQuery = buildVulnerabilityQuery(
      primaryBlock,
      assetFilterOptions,
      vulnFilterOptions,
      integrationValues
    );

    // Defense-in-depth: auto-exclude Deleted status for Vulnerability queries
    // when no status filter has been applied by the user.
    if (
      vulnerabilityQuery &&
      !vulnerabilityQuery.node.filters.some(f => f.field === 'status')
    ) {
      vulnerabilityQuery.node.filters.push({
        field: 'status',
        operator: '=',
        value: 'D',
        not: true,
      });
    }

    if (!vulnerabilityQuery || validBlocks.length === 1) {
      return vulnerabilityQuery;
    }

    buildInterEntityRelationships(
      vulnerabilityQuery.node,
      validBlocks,
      interGroupOperators,
      assetFilterOptions,
      vulnFilterOptions,
      technologyFilterOptions,
      seedFilterOptions,
      integrationValues,
      queryContentTab
    );

    return vulnerabilityQuery;
  }

  const rootNode = buildNodeFromBlock(
    primaryBlock,
    assetFilterOptions,
    vulnFilterOptions,
    technologyFilterOptions,
    seedFilterOptions,
    integrationValues,
    queryContentTab
  );
  if (!rootNode) {
    return null;
  }

  if (validBlocks.length > 1) {
    buildInterEntityRelationships(
      rootNode,
      validBlocks,
      interGroupOperators,
      assetFilterOptions,
      vulnFilterOptions,
      technologyFilterOptions,
      seedFilterOptions,
      integrationValues,
      queryContentTab
    );
  }

  if (riskKey && primaryBlock.entityType === 'Asset') {
    if (!rootNode.filters.some(f => f.field === 'status')) {
      rootNode.filters.push({
        field: 'status',
        operator: '=',
        value: 'D',
        not: true,
      });
    }

    rootNode.relationships.push({
      label: ['HAS_WEBPAGE', 'HAS_PORT'],
      target: {
        labels: [],
        filters: [],
        relationships: [
          {
            label: 'HAS_VULNERABILITY',
            target: {
              labels: ['Risk'],
              filters: [
                {
                  field: 'key',
                  operator: '=',
                  value: riskKey,
                },
              ],
            },
          },
        ],
      },
    });
  }

  // Defense-in-depth: auto-exclude Deleted status for Asset and Seed entity types
  // when no status filter is already applied. The backend enforces ForceExcludeDeleted
  // for non-Praetorian users, but adding the frontend filter prevents stale/cached
  // deleted items from appearing in query results.
  // Note: 'Vulnerability' entity type is handled by buildVulnerabilityQuery (returns early
  // above). Asset without riskKey and Seed both benefit from this general exclusion.
  if (
    (primaryBlock.entityType === 'Asset' ||
      primaryBlock.entityType === 'Seed') &&
    !rootNode.filters.some(f => f.field === 'status')
  ) {
    rootNode.filters.push({
      field: 'status',
      operator: '=',
      value: 'D',
      not: true,
    });
  }

  const graphQuery: GraphQuery = {
    node: rootNode,
    limit: 100,
    page: 0,
  };

  return graphQuery;
}
